/**
 * PURPOSE:
 * Render the event configuration, sports, medal table, Cómputo General and event-specific draw tabs.
 *
 * RESPONSIBILITIES:
 * - Validate company-scoped access to the selected event.
 * - Load event metadata and the sports available to the authenticated user.
 * - Render SorteoArea for event 16 and SorteoArea2 for event 17.
 * - FTR-0066: show the "Cómputo" tab only to rol 1 when the event has flag_computo_general = 1, and show/hide it
 *   right after "Ajustes" saves the flag (no page reload). UI hint only: the backend enforces rol 1 and the flag.
 *
 * COLLABORATORS:
 * - ConfigEventArea
 * - MedalTableArea
 * - ComputoGeneralArea
 * - SorteoArea
 * - SorteoArea2
 * - CardSport
 *
 * ROUTE:
 * src/sections/ListCategoriasSection.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../config/config.js";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import CardSport from "../components/Cards/CardSport/CardSport";
import styled from "@emotion/styled";
import ConfigEventArea from "../area/ConfigEventArea/ConfigEventArea";
import SorteoArea from "../area/SorteoArea/SorteoArea";
import SorteoArea2 from "../area/SorteoArea/SorteoArea2";
import MedalTableArea from "../area/MedalTableArea/MedalTableArea";
import ComputoGeneralArea from "../area/ComputoGeneralArea/ComputoGeneralArea";
import { FiSearch } from "react-icons/fi";
import { getMenuSportsForEvent } from "../services/menuSportsService.js";

const CustomTabList = styled(TabList)`
  .MuiTab-root {
    min-width: 100px;
    color: #616161;
    font-weight: bold;
    text-transform: uppercase;
    transition: color 0.3s;
    &:hover {
      color: #1e88e5;
    }
    &.Mui-selected {
      color: #1e88e5;
    }
  }
  .MuiTabs-indicator {
    background-color: #1e88e5;
    height: 4px;
  }
`;

const CustomTabPanel = styled(TabPanel)`
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
`;

const parsePositiveInt = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const getUserRole = (user) => {
  const rawRole = user?.rol ?? user?.role;
  const parsed = Number(rawRole);
  return Number.isFinite(parsed) ? parsed : null;
};

const isOneCompanyUser = (user) => {
  return user?.is_one_company === true || Number(user?.is_one_company) === 1;
};

const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const ListCategoriasSection = () => {
  const [value, setValue] = useState("2");
  const [sports, setSports] = useState([]);
  const [nameEvent, setNameEvent] = useState("Nombre del Evento");
  const [typeDimension, setTypeDimension] = useState(1);
  const [subTitleSport, setSubTitleSport] = useState("DEPORTES");
  const [eventBanner, setEventBanner] = useState("");
  const [sportSearch, setSportSearch] = useState("");
  const [flagComputoGeneral, setFlagComputoGeneral] = useState(false);
  const { idevent } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const yearSelected = useSelector((state) => state.year.yearSelected);

  const eventId = Number(idevent);
  const hasSorteo = eventId === 16 || eventId === 17;
  const isAdmin = getUserRole(user) === 1;
  const canSeeComputo = isAdmin && flagComputoGeneral;
  // Una pestaña que deja de estar permitida no se pinta ni un render: se muestra "Deportes" hasta que el efecto la ajuste.
  const activeTab =
    (value === "3" && !isAdmin) || (value === "5" && !canSeeComputo) ? "2" : value;
  const filteredSports = useMemo(() => {
    const query = normalizeSearchText(sportSearch);
    if (!query) return sports;
    return sports.filter((sport) =>
      normalizeSearchText(
        [sport.displayName, sport.name, sport.acronym].filter(Boolean).join(" ")
      ).includes(query)
    );
  }, [sports, sportSearch]);

  const handleChange = (event, newValue) => {
    if (newValue === "3" && !isAdmin) return;
    if (newValue === "5" && !canSeeComputo) return;
    setValue(newValue);
  };

  // FTR-0066: "Ajustes" avisa lo que guardó el backend; solo un flag realmente guardado cambia la pestaña.
  const handleEventSaved = useCallback((result) => {
    const computo = result?.computoGeneral;
    if (computo?.applied === true) setFlagComputoGeneral(computo.enabled === true);
  }, []);

  useEffect(() => {
    if (!isAdmin && value === "3") setValue("2");
    if (!canSeeComputo && value === "5") setValue("2");
  }, [isAdmin, canSeeComputo, value]);
  useEffect(() => {
    let cancelled = false;

    const fetchSports = async () => {
      try {
        const eventResponse = await axios.get(
          `${API_BASE_URL}/championship/?idevent=${idevent}`
        );

        const eventInfo = eventResponse.data || {};
        const role = getUserRole(user);
        const userCompanyId = parsePositiveInt(user?.idcompany);
        const eventCompanyId = parsePositiveInt(eventInfo?.idcompany);
        const oneCompany = isOneCompanyUser(user);
        const isCompanyRestrictedUser = role === 4 || (role === 1 && oneCompany);

        if (isCompanyRestrictedUser) {
          if (!userCompanyId) {
            if (!cancelled) setSports([]);
            navigate("/campeonatos", { replace: true });
            return;
          }

          if (eventCompanyId && eventCompanyId !== userCompanyId) {
            if (!cancelled) setSports([]);
            navigate(`/campeonatos?idcompany=${userCompanyId}`, {
              replace: true,
            });
            return;
          }
        }

        if (cancelled) return;

        setEventBanner(eventInfo.event_banner);
        setNameEvent(eventInfo ? eventInfo.name : "Evento");
        setSubTitleSport(
          eventInfo && eventInfo.subTitleSports
            ? eventInfo.subTitleSports
            : "DEPORTES"
        );
        setTypeDimension(
          eventInfo && eventInfo.typeDimension ? eventInfo.typeDimension : 1
        );
        setFlagComputoGeneral(Number(eventInfo?.flag_computo_general) === 1);

        const menuSports = await getMenuSportsForEvent({
          iduser: user.iduser,
          idevent,
        });

        if (!cancelled) {
          setSports(menuSports);
        }
      } catch (error) {
        console.error("Error fetching sports", error);
        if (!cancelled) {
          setSports([]);
          setFlagComputoGeneral(false);
        }
      }
    };

    if (user && idevent) {
      fetchSports();
    }

    return () => {
      cancelled = true;
    };
  }, [user, idevent, navigate]);

  return (
    <div className="flex w-full flex-col items-center">
      {eventBanner && (
        <div className="mb-2 flex w-full justify-center">
          <img
            src={eventBanner}
            alt="Banner"
            className="h-auto w-full sm:w-full md:w-9/10 lg:w-9/10 xl:w-9/10"
            style={{ objectFit: "cover", width: "92%" }}
          />
        </div>
      )}

      <div className="text-center">
        <h2 className="text-3xl font-bold text-blue-600">{nameEvent}</h2>
      </div>
      <div className="mt-4 w-full max-w-6xl">
        <Box sx={{ width: "100%", typography: "body1" }}>
          <TabContext value={activeTab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <CustomTabList
                onChange={handleChange}
                aria-label="Configuración del evento"
                centered
              >
                <Tab label="Ajustes" value="1" />
                <Tab label="Deportes" value="2" />
                {isAdmin && <Tab label="Medallero" value="3" />}
                {hasSorteo && <Tab label="Sorteo" value="4" />}
                {canSeeComputo && <Tab label="Cómputo" value="5" />}
              </CustomTabList>
            </Box>
            <CustomTabPanel value="1">
              <ConfigEventArea onEventSaved={handleEventSaved} />
            </CustomTabPanel>
            <CustomTabPanel value="2">
              <div className="mx-auto mb-5 max-w-xl">
                <label className="relative block">
                  <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-slate-400" />
                  <input
                    type="search"
                    value={sportSearch}
                    onChange={(event) => setSportSearch(event.target.value)}
                    placeholder="Buscar deporte por nombre o sigla..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm font-semibold text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  />
                </label>
                <p className="mt-2 text-center text-xs font-semibold text-slate-400">
                  {filteredSports.length} de {sports.length} deportes
                </p>
              </div>
              <div
                className={`grid grid-cols-1 justify-items-center gap-4 md:grid-cols-2 ${
                  typeDimension === 2 ? "lg:grid-cols-4" : "lg:grid-cols-3"
                }`}
              >
                {filteredSports.map((sport) => (
                  <div key={sport.idsport}>
                    <CardSport sport={sport} typeDimension={typeDimension} />
                  </div>
                ))}
              </div>
              {filteredSports.length === 0 ? (
                <div className="mx-auto mt-5 max-w-lg rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm font-semibold text-slate-500">
                  No se encontraron deportes con esa búsqueda.
                </div>
              ) : null}
            </CustomTabPanel>
            {isAdmin && (
              <CustomTabPanel value="3">
                <MedalTableArea />
              </CustomTabPanel>
            )}
            {hasSorteo && (
              <CustomTabPanel value="4">
                {eventId === 16 && <SorteoArea />}
                {eventId === 17 && <SorteoArea2 />}
              </CustomTabPanel>
            )}
            {canSeeComputo && (
              <CustomTabPanel value="5">
                <ComputoGeneralArea />
              </CustomTabPanel>
            )}
          </TabContext>
        </Box>
      </div>
    </div>
  );
};

export default ListCategoriasSection;
